import { useState, useEffect } from 'react';
import { initI18n } from '@/core/config/i18n';

export const LANGUAGE_STORAGE_KEY = 'stilingue:language';
export const LANGUAGE_CHANGE_EVENT = 'mfe-change-language';
export const LEGACY_LANGUAGE_CHANGE_EVENT = 'set-language';

// The browser's 'storage' event only fires in OTHER windows/tabs, not in the window that
// made the change. Legacy (non-MFE) headers in consuming apps write to localStorage directly
// without dispatching LANGUAGE_CHANGE_EVENT, so this patch ensures the same-window listeners
// also react to those writes.
//
// Storage.prototype.setItem is patched (rather than the localStorage instance) because
// jsdom exposes localStorage as an exotic object where instance-level property assignment
// may be silently ignored.
let _originalSetItem: typeof Storage.prototype.setItem | null = null;

export const installSameWindowStorageListener = () => {
  if (_originalSetItem !== null || typeof Storage === 'undefined') return;

  _originalSetItem = Storage.prototype.setItem;
  Storage.prototype.setItem = function (key: string, value: string) {
    _originalSetItem!.call(this, key, value);
    if (key === LANGUAGE_STORAGE_KEY) {
      document.dispatchEvent(
        new CustomEvent(LANGUAGE_CHANGE_EVENT, {
          detail: { language: value },
          bubbles: true,
          composed: true,
        }),
      );
    }
  };
};

/**
 * Normalizes locale strings to one of the supported base languages: 'pt', 'en', or 'es'.
 * Handles variations like 'en_us', 'pt-BR', 'es-ES' from localStorage and URL parameters.
 */
export const normalizeLocale = (locale: string | null): string => {
  if (!locale) return 'pt';
  const cleanLocale = locale.toLowerCase().trim();

  if (cleanLocale.startsWith('en')) return 'en';
  if (cleanLocale.startsWith('pt')) return 'pt';
  if (cleanLocale.startsWith('es')) return 'es';

  // Fallback to default language if the locale is not one of the supported ones
  return 'pt';
};

interface UseAppLanguageOptions {
  /**
   * If true, forces 'pt' regardless of stored values.
   * Useful for specific header configurations where language selection is disabled.
   */
  forceDefault?: boolean;
}

/**
 * Hook to manage application-wide language logic.
 * Handles initialization, URL parameters, localStorage, and reactive updates across windows/tabs.
 */
export const useAppLanguage = (options: UseAppLanguageOptions = {}) => {
  const { forceDefault = false } = options;

  const getTargetLocale = () => {
    if (forceDefault) return 'pt';

    // Check URL first
    const params = new URLSearchParams(globalThis.location?.search || '');
    const urlLocale = params.get('locale');
    if (urlLocale) return normalizeLocale(urlLocale);

    // Then localStorage
    return normalizeLocale(localStorage.getItem(LANGUAGE_STORAGE_KEY));
  };

  const [locale, setLocale] = useState(getTargetLocale);

  // Install the same-window localStorage interceptor once. This ensures legacy headers
  // in the consuming app that write directly to localStorage (without dispatching
  // LANGUAGE_CHANGE_EVENT) still trigger a reactive update in the same window.
  useEffect(() => {
    installSameWindowStorageListener();
  }, []);

  // Keeps localStorage aligned with URL-driven locale resolution so all consumers
  // (including those reading storage directly) share the same language source.
  useEffect(() => {
    if (forceDefault) {
      return;
    }

    const params = new URLSearchParams(globalThis.location?.search || '');
    const urlLocale = params.get('locale');

    if (urlLocale) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizeLocale(urlLocale));
    }
  }, [forceDefault]);

  // Initialize i18n with the resolved locale. initI18n handles both initialization
  // and changing language if i18n is already initialized, avoiding redundant changeLanguage calls.
  useEffect(() => {
    initI18n(locale);
  }, [locale]);

  // Sync with other tabs via StorageEvent, and with the same window via language events.
  // The browser's StorageEvent only fires in OTHER windows, so the custom event is required
  // to react to language changes dispatched by the TopBar in the same window.
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LANGUAGE_STORAGE_KEY && !forceDefault) {
        setLocale(normalizeLocale(e.newValue));
      }
    };

    const handleLanguageEvent = (e: Event) => {
      if (!forceDefault) {
        const detail = (e as CustomEvent<{ language: string }>).detail;
        setLocale(normalizeLocale(detail?.language));
      }
    };

    const handleLegacyLanguageEvent = (e: Event) => {
      if (forceDefault) {
        return;
      }

      const detail = (e as CustomEvent<{ currentLocale?: string; language?: string }>).detail;
      const nextLocale = normalizeLocale(detail?.currentLocale ?? detail?.language ?? null);

      setLocale(nextLocale);

      // Keep shared storage in sync when legacy consumers only emit `set-language`.
      localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLocale);
    };

    window.addEventListener('storage', handleStorageChange);
    document.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageEvent);
    document.addEventListener(LEGACY_LANGUAGE_CHANGE_EVENT, handleLegacyLanguageEvent);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageEvent);
      document.removeEventListener(LEGACY_LANGUAGE_CHANGE_EVENT, handleLegacyLanguageEvent);
    };
  }, [forceDefault]);

  return { locale, setLocale };
};
